import { _ as __rest } from '../tslib.es6-C8rd5pYo.mjs';
import { css, jsx } from '@emotion/react';
import { Colors, Typography, Spinner, Icon, Shadows } from '@zapier/design-system';
import { Description } from '../Description/Description.es.js';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage.es.js';
import { Label } from '../Label/Label.es.js';
import { forwardRef } from 'react';
import { useFilter } from 'react-aria';
import { ComboBox, Input, Popover, ListBox } from 'react-aria-components';
import { TypeaheadItem } from './TypeaheadItem.es.js';
import { TypeaheadRightSlot } from './TypeaheadRightSlot.es.js';

const ComboBoxStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: `var(--zds-space-4, 5px)`
});
const InputWrapperStyles = css({
  position: "relative"
});
const IconLeftWrapperStyles = css({
  position: "absolute",
  top: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: `var(--zds-size-large, 44px)`,
  height: `var(--zds-size-large, 44px)`,
  color: `var(--zds-stroke-stronger, ${Colors.GrayWarm6})`,
  '&[data-size="compact"], &[data-size="medium"]': {
    width: `var(--zds-size-medium, 36px)`,
    height: `var(--zds-size-medium, 36px)`
  }
});
const InputStyles = css(Object.assign(Object.assign({}, Typography.Body2), { boxSizing: "border-box", margin: 0, height: `var(--zds-size-large, 44px)`, border: `1px solid var(--zds-stroke-default, ${Colors.GrayWarm6})`, borderRadius: "var(--zds-radius-small, 3px)", padding: `var(--zds-space-12, 10px) var(--zds-space-40, 40px)`, backgroundColor: `var(--zds-background-weaker, ${Colors.PrimeWhite})`, fontSize: `var(--zds-font-size-default, 16px)`, color: `var(--zds-text-default, ${Colors.GrayWarm10})`, verticalAlign: "middle", width: "100%", '&[data-size="compact"]': Object.assign({}, Typography.MinimalPrint2), '&[data-size="compact"], &[data-size="medium"]': {
  height: `var(--zds-size-medium, 36px)`,
  padding: `var(--zds-space-6, 10px) var(--zds-space-36, 40px)`
}, "&::placeholder": {
  color: `var(--zds-text-weaker, ${Colors.GrayWarm6})`
}, "&[data-focused]": {
  outline: `var(--zds-border-focus, 2px solid ${Colors.UiPrimaryStrongest})`,
  outlineOffset: "-2px"
}, "&[data-disabled]": {
  backgroundColor: `var(--zds-background-strongest, ${Colors.GrayWarm2})`,
  color: `var(--zds-text-weakest, ${Colors.GrayWarm6})`,
  borderColor: `var(--zds-stroke-weaker, ${Colors.GrayWarm6})`,
  cursor: "not-allowed"
}, "&[data-invalid]": {
  borderColor: `var(--zds-status-error, ${Colors.StatusErrorStronger})`
} }));
const InputNoLeftIconStyles = css({
  padding: `var(--zds-space-8, 10px) var(--zds-space-12, 15px)`,
  '&[data-size="compact"], &[data-size="medium"]': {
    padding: `var(--zds-space-8, 10px) var(--zds-space-6, 15px)`
  }
});
const PopoverStyles = ({ menuWidth }) => css({
  // --trigger-width variable is provided by react-aria Popover.
  width: menuWidth !== null && menuWidth !== void 0 ? menuWidth : "var(--trigger-width)",
  backgroundColor: `var(--zds-brand-almost-white, ${Colors.GrayWarm1})`,
  boxShadow: `var(--zds-shadow-surface, ${Shadows.elevation20})`,
  overflow: "auto"
});
const ListBoxStyles = css({
  display: "block",
  width: "unset",
  maxHeight: "inherit",
  minHeight: "unset",
  border: "none"
});
const TypeaheadRoot = forwardRef((_a, ref) => {
  var _b;
  var { children, className, classNamePopOver, defaultItems, defaultSelectedKey, defaultInputValue, errorMessage, helpText, iconLeft = "navSearch", inputValue, isBusy = false, isDisabled = false, isInvalid = false, items, label, menuWidth, menuTrigger, onSelectionChange, placeholder, iconRight = null, selectedKey, size = "large" } = _a, props = __rest(_a, ["children", "className", "classNamePopOver", "defaultItems", "defaultSelectedKey", "defaultInputValue", "errorMessage", "helpText", "iconLeft", "inputValue", "isBusy", "isDisabled", "isInvalid", "items", "label", "menuWidth", "menuTrigger", "onSelectionChange", "placeholder", "iconRight", "selectedKey", "size"]);
  const { contains } = useFilter({ sensitivity: "base" });
  const defaultFilter = (_b = props.defaultFilter) !== null && _b !== void 0 ? _b : contains;
  return jsx(
    ComboBox,
    Object.assign({}, props, { className, css: ComboBoxStyles, defaultFilter, defaultItems, defaultSelectedKey, defaultInputValue, inputValue, isDisabled, isInvalid, items, menuTrigger, onSelectionChange, selectedKey, allowsEmptyCollection: true, "data-zds": true, shouldFocusWrap: true }),
    label && jsx(Label, { isCompact: size === "compact", isDisabled, isInvalid }, label),
    jsx(
      "div",
      { css: InputWrapperStyles, "data-zds": true },
      iconLeft && jsx("div", { css: IconLeftWrapperStyles, "data-size": size, "data-zds": true }, isBusy ? jsx(Spinner, null) : jsx(Icon, { name: iconLeft, size: size === "compact" ? "20px" : "24px" })),
      jsx(Input, { css: [InputStyles, iconLeft === null && InputNoLeftIconStyles], "data-zds": true, "data-size": size, placeholder, ref }),
      jsx(TypeaheadRightSlot, { iconRight, size })
    ),
    isInvalid && errorMessage ? jsx(ErrorMessage, { isCompact: size === "compact" }, errorMessage) : helpText && jsx(Description, { isCompact: size === "compact" }, helpText),
    jsx(
      Popover,
      { className: classNamePopOver, css: PopoverStyles({ menuWidth }), "data-zds": true, offset: 5 },
      jsx(ListBox, { css: ListBoxStyles, "data-zds": true }, typeof children === "function" ? (item) => children(item) : children)
    )
  );
});
const Typeahead = Object.assign(TypeaheadRoot, {
  Item: TypeaheadItem
});

export { Typeahead, TypeaheadRoot };
//# sourceMappingURL=Typeahead.es.js.map
